import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  Skeleton,
  useTheme,
  Button,
  Paper,
  alpha,
  Stack,
  Grid,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useUpdateClientStage } from "../../hooks/useClientsGroups";
import {
  useAwarenessPipelineStages,
  useAwarenessPipelineClients,
} from "../../hooks/useAwarenessPipeline";
import { useAwarenessPipelineNavigation } from "../../hooks/useAwarenessPipelineNavigation";
import { AWARENESS_UNSET_GROUP_STAGE_ID } from "../../data/awarenessPipeline/constants";
import { useAuthContext } from "../../context/AuthContext";
import {
  AWARENESS_PERSON_FILTERS,
  clientMatchesAwarenessPersonFilter,
  normalizeAwarenessPersonFilter,
  readAwarenessPersonFilter,
  writeAwarenessPersonFilter,
} from "../../utils/awarenessPersonFilter";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useTranslation } from "react-i18next";
import CardTagsRow from "../../components/SalesPipeline/CardTagsRow";
import ClientRelationshipBadge from "../../components/Chat/ClientRelationshipBadge";
import ClientAdvancedFiltersDialog from "../../components/clients/ClientAdvancedFiltersDialog";
import AwarenessClientSearchBar from "../../components/Pipeline/AwarenessClientSearchBar";
import AwarenessClientPreviewDialog from "../../components/Pipeline/AwarenessClientPreviewDialog";
import { useClientFilters } from "../../hooks/useClientFilters";
import { getClientListRelationshipProps } from "../../utils/clientRelationships";
import { formatClientDisplayPhone } from "../../utils/phone";

const EXPLICACAO_NIVEIS_CONSCIENCIA =
  "Os níveis de consciência são etapas que representam em que estágio do relacionamento ou da jornada de compra o cliente se encontra — por exemplo: ainda não conhece o problema, está considerando soluções ou já decidiu comprar. Eles ajudam a organizar o pipeline de clientes e o atendimento. Para usar esta tela, cadastre primeiro as etapas (níveis) na tela de cadastro.";

const AWARENESS_COLUMN_WIDTH = 300;
const AWARENESS_CARD_FALLBACK_WIDTH = 260;

const getClientInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const lockAwarenessDragStyle = (style, width) => ({
  ...style,
  boxSizing: "border-box",
  width,
  maxWidth: width,
  minWidth: width,
});

const isElementOverflowing = (el) => {
  if (!el) return false;
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
};

/** Mostra o texto completo no hover só quando o conteúdo está cortado com reticências. */
const OverflowTooltip = ({ title, children }) => {
  const [overflowed, setOverflowed] = useState(false);
  const child = React.Children.only(children);

  const measure = (node) => {
    setOverflowed(isElementOverflowing(node));
  };

  return (
    <Tooltip
      title={title || ""}
      disableHoverListener={!overflowed || !title}
      enterDelay={300}
      placement="top"
      slotProps={{
        tooltip: {
          sx: { maxWidth: 360, whiteSpace: "pre-wrap" },
        },
      }}
    >
      {React.cloneElement(child, {
        ref: (node) => {
          const prev = child.ref;
          if (typeof prev === "function") prev(node);
          else if (prev && typeof prev === "object") prev.current = node;
          if (node) measure(node);
        },
        onMouseEnter: (event) => {
          measure(event.currentTarget);
          child.props.onMouseEnter?.(event);
        },
      })}
    </Tooltip>
  );
};

const Pipeline = ({ workspaceBasePath } = {}) => {
  const theme = useTheme();
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const embeddedInWorkspace = Boolean(workspaceBasePath);
  const { userData } = useAuthContext() || {};
  const userId = userData?.id;
  const [personType, setPersonType] = useState(() => readAwarenessPersonFilter(userId));
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const {
    filters,
    patchFilters,
    applyFilters,
    clearFilters,
    resetDraftToApplied,
    draftActiveCount,
    queryString: filtersQuery,
    activeCount: activeFilterCount,
  } = useClientFilters();

  useEffect(() => {
    setPersonType(readAwarenessPersonFilter(userId));
  }, [userId]);

  const applySearchRef = useRef(setAppliedSearch);
  applySearchRef.current = setAppliedSearch;
  const debouncedApplySearch = useMemo(
    () =>
      debounce((value) => {
        applySearchRef.current(String(value || "").trim());
      }, 800),
    []
  );
  useEffect(() => () => debouncedApplySearch.cancel(), [debouncedApplySearch]);

  const handleSearchInputChange = useCallback(
    (value) => {
      setSearchInput(value);
      debouncedApplySearch(String(value || "").trim());
    },
    [debouncedApplySearch]
  );

  const {
    data: pipelineStages,
    isLoading: isLoadingStages,
    error: errorStages,
    refetch: refetchStages,
  } = useAwarenessPipelineStages(personType, {
    search: appliedSearch,
    filtersQuery,
  });
  const { visibleStages, path, drillDown, goToRoot, goToPathIndex, hasChildren } =
    useAwarenessPipelineNavigation(pipelineStages || []);
  const {
    clientsByStage,
    loading: isLoadingClients,
    error: errorClients,
    hasMoreByStage,
    loadingByStage,
    totalByStage,
    loadMore,
    moveClientBetweenStages,
    patchClient,
    refreshAll,
  } = useAwarenessPipelineClients({
    stages: visibleStages,
    personType,
    search: appliedSearch,
    filtersQuery,
  });
  const hasListFilter =
    Boolean(filtersQuery) ||
    String(appliedSearch || "").trim().length >= 3 ||
    personType === "pf" ||
    personType === "pj";
  const visibleClientsByStage = useMemo(() => {
    const next = {};
    Object.entries(clientsByStage || {}).forEach(([stageId, list]) => {
      next[stageId] = (list || []).filter((client) =>
        clientMatchesAwarenessPersonFilter(client, personType)
      );
    });
    return next;
  }, [clientsByStage, personType]);
  const updateClientStage = useUpdateClientStage();
  const suppressCardOpenRef = useRef(false);
  const dragSizeRef = useRef({ width: AWARENESS_CARD_FALLBACK_WIDTH });
  const [draggingId, setDraggingId] = useState(null);
  const [previewClient, setPreviewClient] = useState(null);
  const [previewStageName, setPreviewStageName] = useState("");

  const openClientPreview = useCallback((client, stageName = "") => {
    if (!client || suppressCardOpenRef.current) return;
    setPreviewClient(client);
    setPreviewStageName(stageName);
  }, []);

  const onDragStart = useCallback((start) => {
    suppressCardOpenRef.current = true;
    document.body.style.cursor = "grabbing";
    setDraggingId(start.draggableId);
    const el = document.querySelector(
      `[data-rbd-draggable-id="${start.draggableId}"]`
    );
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) {
      dragSizeRef.current = { width: Math.round(rect.width) };
    }
  }, []);

  useEffect(
    () => () => {
      document.body.style.cursor = "";
    },
    []
  );

  const handlePersonTypeChange = useCallback(
    (_event, value) => {
      if (!value) return;
      const next = normalizeAwarenessPersonFilter(value);
      setPersonType(next);
      writeAwarenessPersonFilter(userId, next);
    },
    [userId]
  );

  const onDragEnd = useCallback(
    (result) => {
      document.body.style.cursor = "";
      setDraggingId(null);
      window.setTimeout(() => {
        suppressCardOpenRef.current = false;
      }, 0);

      if (!result.destination) return;

      const { source, destination, draggableId } = result;
      const sourceStage = source.droppableId;
      const destStage = destination.droppableId;

      if (sourceStage === destStage) return;

      const clientId = Number(draggableId);
      const newStageId = Number(destStage);
      if (!Number.isFinite(clientId)) return;

      moveClientBetweenStages(Number(sourceStage), newStageId, clientId);

      updateClientStage.mutate(
        {
          clientId,
          newStageId: newStageId === AWARENESS_UNSET_GROUP_STAGE_ID ? null : newStageId,
        },
        {
          onSuccess: () => {
            refetchStages();
          },
          onError: () => {
            console.error("Erro ao atualizar estágio do cliente");
            refreshAll();
            refetchStages();
          },
        }
      );
    },
    [moveClientBetweenStages, updateClientStage, refetchStages, refreshAll]
  );

  if (isLoadingStages || (isLoadingClients && !pipelineStages?.length)) {
    return (
      <Box className="tour-pipeline-root" sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="70%" height={28} sx={{ mb: 3 }} />
        <Divider sx={{ mb: 3, backgroundColor: theme.palette.divider }} />
        <Box sx={{ display: "flex", overflowX: "auto", gap: 2 }}>
          {[1, 2, 3, 4].map((col) => (
            <Box
              key={col}
              sx={{
                minWidth: 300,
                flex: "0 0 300px",
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                p: 2,
                bgcolor: "background.paper",
              }}
            >
              <Skeleton variant="text" width="70%" height={32} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 2 }} />
              <Divider sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="100%" height={8} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rounded" width="100%" height={100} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (errorClients || errorStages) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: embeddedInWorkspace ? "100%" : "100vh" }}>
        <Typography color="error">Erro ao carregar dados. Tente novamente.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: embeddedInWorkspace
          ? "100%"
          : "calc(100vh - var(--app-chrome-offset, 64px))",
        minHeight: 0,
        px: 3,
        pt: 3,
        pb: 0,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", sm: "flex-start" },
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 0.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
          <Typography variant="h4" gutterBottom color={theme.palette.text.primary}>
            {t("pipelinePage.awarenessHeading")}
          </Typography>
          <Typography
            variant="body1"
            color={theme.palette.text.secondary}
            gutterBottom
          >
            {t("pipelinePage.awarenessSubtitle")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            flexWrap: "wrap",
            flex: { xs: "1 1 100%", sm: "0 1 auto" },
          }}
        >
          <AwarenessClientSearchBar
            value={searchInput}
            onChange={handleSearchInputChange}
            onOpenAdvanced={() => setFiltersOpen(true)}
            activeFilterCount={activeFilterCount}
          />
        <Tooltip title={t("pipelinePage.personFilterHint")}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={personType}
            onChange={handlePersonTypeChange}
            aria-label={t("pipelinePage.personFilterLabel")}
            sx={{
              alignSelf: { xs: "flex-start", sm: "center" },
              flexShrink: 0,
              "& .MuiToggleButton-root": {
                px: 1.25,
                py: 0.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                borderColor: alpha(theme.palette.divider, 0.8),
                color: "text.secondary",
                "&.Mui-selected": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.18),
                  },
                },
              },
            }}
          >
            <ToggleButton value={AWARENESS_PERSON_FILTERS.ALL}>
              {t("pipelinePage.personFilterAll")}
            </ToggleButton>
            <ToggleButton value={AWARENESS_PERSON_FILTERS.PF}>
              {t("pipelinePage.personFilterPf")}
            </ToggleButton>
            <ToggleButton value={AWARENESS_PERSON_FILTERS.PJ}>
              {t("pipelinePage.personFilterPj")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
        </Box>
      </Box>
      <Breadcrumbs sx={{ mb: 1.5, flexShrink: 0 }} aria-label="breadcrumb">
        <Link
          underline="hover"
          color="inherit"
          onClick={goToRoot}
          sx={{ cursor: "pointer", fontWeight: path.length ? 500 : 700 }}
        >
          {t("pipelinePage.root")}
        </Link>
        {path.map((node, index) => {
          const isLast = index === path.length - 1;
          return isLast ? (
            <Typography key={node.id} color="text.primary" fontWeight={700}>
              {node.nome}
            </Typography>
          ) : (
            <Link
              key={node.id}
              underline="hover"
              color="inherit"
              onClick={() => goToPathIndex(index)}
              sx={{ cursor: "pointer" }}
            >
              {node.nome}
            </Link>
          );
        })}
      </Breadcrumbs>
      <Divider sx={{ mb: 2, backgroundColor: theme.palette.divider }} />
      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Box
          className="tour-pipeline-board"
          sx={{
            display: "flex",
            overflowX: "auto",
            overflowY: visibleStages?.length ? "hidden" : "auto",
            gap: 2,
            px: 0.5,
            py: 1.5,
            pb: 2,
            width: "100%",
            flex: 1,
            minHeight: 0,
            alignItems: visibleStages?.length ? "stretch" : "flex-start",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.text.primary, 0.12),
              borderRadius: 999,
            },
          }}
        >
          {visibleStages?.length ? (
            visibleStages
              .filter((stage) => {
                if (!stage.remainder && !stage.virtual && Number(stage.id) !== AWARENESS_UNSET_GROUP_STAGE_ID) {
                  return true;
                }
                const key = String(stage.id);
                if (totalByStage[key] != null) return Number(totalByStage[key]) > 0;
                if (loadingByStage[key]) return Number(stage.total) > 0;
                const loaded = visibleClientsByStage[key] ?? clientsByStage[key];
                if (Array.isArray(loaded)) return loaded.length > 0;
                return Number(stage.total) > 0;
              })
              .map((stage) => (
              <PipelineStage
                key={String(stage.id)}
                stage={stage}
                clients={visibleClientsByStage[String(stage.id)] ?? []}
                stageTotalCount={
                  totalByStage[String(stage.id)] ??
                  (hasListFilter
                    ? undefined
                    : stage.remainder
                      ? stage.total
                      : stage.subtreeTotal ?? stage.total)
                }
                hasMore={hasMoreByStage[String(stage.id)]}
                loadingMore={loadingByStage[String(stage.id)]}
                onLoadMore={() => loadMore(stage.id)}
                suppressCardOpenRef={suppressCardOpenRef}
                dragSizeRef={dragSizeRef}
                draggingId={draggingId}
                onTitleClick={hasChildren(stage) ? () => drillDown(stage.id) : undefined}
                onOpenClient={openClientPreview}
              />
            ))
          ) : (
            <Paper
              variant="outlined"
              sx={{
                width: "100%",
                maxWidth: 960,
                mx: "auto",
                p: { xs: 3, sm: 4 },
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.25 : 0.18)}`,
                background: theme.palette.mode === "dark"
                  ? `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 50%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`
                  : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.06)} 40%, ${theme.palette.background.paper} 100%)`,
                boxShadow: theme.palette.mode === "dark"
                  ? `0 12px 40px ${alpha(theme.palette.common.black, 0.35)}`
                  : `0 12px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 56,
                        height: 56,
                        flexShrink: 0,
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.12),
                      }}
                    >
                      <LightbulbOutlinedIcon sx={{ fontSize: 32 }} color="primary" />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                        Nenhum nível de consciência cadastrado
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                        {EXPLICACAO_NIVEIS_CONSCIENCIA}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" icon={<PsychologyOutlinedIcon sx={{ fontSize: 18 }} />} label="Jornada do cliente" variant="outlined" sx={{ "& .MuiChip-icon": { color: "primary.main" } }} />
                    <Chip size="small" icon={<TrendingUpOutlinedIcon sx={{ fontSize: 18 }} />} label="Pipeline organizado" variant="outlined" sx={{ "& .MuiChip-icon": { color: "primary.main" } }} />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/awareness-stages")}
                    sx={{
                      py: 1.5,
                      px: 3,
                      fontWeight: 600,
                      borderRadius: 2,
                      boxShadow: 2,
                      "&:hover": { boxShadow: 4 },
                    }}
                  >
                    Ir para cadastro de níveis de consciência
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>
      </DragDropContext>
      <AwarenessClientPreviewDialog
        open={Boolean(previewClient)}
        client={previewClient}
        stageName={previewStageName}
        onClose={() => {
          setPreviewClient(null);
          setPreviewStageName("");
        }}
        onClientUpdated={(patch) => {
          if (!previewClient?.id) return;
          patchClient(previewClient.id, patch);
          setPreviewClient((prev) => (prev ? { ...prev, ...patch } : prev));
        }}
      />
      <ClientAdvancedFiltersDialog
        open={filtersOpen}
        filters={filters}
        draftActiveCount={draftActiveCount}
        onPatch={patchFilters}
        onApply={applyFilters}
        onClear={clearFilters}
        onClose={() => {
          resetDraftToApplied();
          setFiltersOpen(false);
        }}
      />
    </Box>
  );
};

const PipelineStage = ({
  stage,
  clients,
  stageTotalCount,
  hasMore,
  loadingMore,
  onLoadMore,
  suppressCardOpenRef,
  dragSizeRef,
  draggingId,
  onTitleClick,
  onOpenClient,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("settings");
  const count = stageTotalCount ?? clients?.length ?? 0;
  const progressValue = Math.min(100, count > 0 ? (count / 10) * 100 : 0);
  const isRemainderStage = Boolean(stage.remainder);
  const isUnsetStage =
    Boolean(stage.virtual) ||
    isRemainderStage ||
    Number(stage.id) === AWARENESS_UNSET_GROUP_STAGE_ID;
  const canDrill = typeof onTitleClick === "function" && !isRemainderStage;
  const childCount = Array.isArray(stage.children) ? stage.children.length : stage.childCount || 0;
  const displayName = isRemainderStage
    ? t("pipelinePage.unsetSubstage")
    : stage.nome ?? "";
  const stageTitle = `${displayName} (${count})`;
  const stageDescription = isRemainderStage
    ? t("pipelinePage.unsetSubstageDescription", {
        name: stage.remainderName || stage.nome || "",
      })
    : stage.description || stage.descricao || "";
  const accent = isUnsetStage ? theme.palette.text.secondary : theme.palette.primary.main;
  const columnSurface = isUnsetStage
    ? theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.06)
      : alpha(theme.palette.grey[700], 0.1)
    : theme.palette.background.paper;

  return (
    <Box
      sx={{
        flex: `0 0 ${AWARENESS_COLUMN_WIDTH}px`,
        width: AWARENESS_COLUMN_WIDTH,
        minWidth: AWARENESS_COLUMN_WIDTH,
        maxWidth: AWARENESS_COLUMN_WIDTH,
        height: "100%",
        maxHeight: "100%",
        backgroundColor: columnSurface,
        borderRadius: 2,
        border: `1px solid ${
          isUnsetStage
            ? alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.16 : 0.12)
            : theme.palette.divider
        }`,
        boxShadow: isUnsetStage
          ? "none"
          : theme.palette.mode === "dark"
            ? theme.shadows[8]
            : "0 8px 24px rgba(15, 23, 42, 0.08)",
        padding: 2,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 0.5,
          pr: 0.5,
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <OverflowTooltip title={stageTitle}>
          <Typography
            variant="h6"
            noWrap
            onClick={onTitleClick}
            sx={{
              fontWeight: 700,
              color: accent,
              flex: 1,
              minWidth: 0,
              cursor: canDrill ? "pointer" : "default",
              textDecoration: canDrill ? "underline" : "none",
              userSelect: "none",
            }}
          >
            {stageTitle}
          </Typography>
        </OverflowTooltip>
        {canDrill ? (
          <Tooltip title={t("pipelinePage.openSublevels")}>
            <ChevronRightRoundedIcon
              onClick={onTitleClick}
              sx={{ fontSize: 22, flexShrink: 0, color: accent, cursor: "pointer" }}
            />
          </Tooltip>
        ) : null}
      </Box>
      {canDrill ? (
        <Chip
          size="small"
          variant="outlined"
          label={t("pipelinePage.sublevelCount", { count: childCount })}
          onClick={onTitleClick}
          sx={{ mb: 1, alignSelf: "flex-start", flexShrink: 0, cursor: "pointer" }}
        />
      ) : null}
      {stage.legacy && !stage.virtual ? (
        <Chip
          size="small"
          label="Legado"
          variant="outlined"
          sx={{ mb: 1, alignSelf: "flex-start", flexShrink: 0 }}
        />
      ) : null}
      {stageDescription ? (
        <OverflowTooltip title={stageDescription}>
          <Typography
            variant="body2"
            color={theme.palette.text.secondary}
            sx={{
              mb: 2,
              minHeight: 0,
              flexShrink: 0,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {stageDescription}
          </Typography>
        </OverflowTooltip>
      ) : (
        <Box sx={{ mb: 1.5, flexShrink: 0 }} />
      )}
      <Divider sx={{ mb: 2, flexShrink: 0 }} />
      <LinearProgress
        variant="determinate"
        value={progressValue}
        sx={{
          height: 6,
          borderRadius: 5,
          mb: 2,
          flexShrink: 0,
          bgcolor: alpha(accent, 0.12),
          "& .MuiLinearProgress-bar": {
            borderRadius: 5,
            bgcolor: isUnsetStage ? alpha(theme.palette.text.secondary, 0.55) : undefined,
          },
        }}
      />
      <Droppable
        droppableId={String(stage.id)}
        type="CARD"
        ignoreContainerClipping
        renderClone={(provided, snapshot, rubric) => {
          const client =
            (Array.isArray(clients) &&
              clients.find((item) => String(item.id) === rubric.draggableId)) ||
            clients?.[rubric.source.index];
          const width = dragSizeRef?.current?.width || AWARENESS_CARD_FALLBACK_WIDTH;
          if (!client) {
            return (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={lockAwarenessDragStyle(provided.draggableProps.style, width)}
              />
            );
          }
          return (
            <PipelineCard
              provided={provided}
              snapshot={snapshot}
              client={client}
              clone
              dragWidth={width}
            />
          );
        }}
      >
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 64,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              mx: -0.5,
              px: 0.5,
              pt: 0.5,
              borderRadius: 1.5,
              bgcolor: snapshot.isDraggingOver
                ? alpha(theme.palette.primary.main, 0.07)
                : "transparent",
              outline: snapshot.isDraggingOver
                ? `2px dashed ${alpha(theme.palette.primary.main, 0.35)}`
                : "2px dashed transparent",
              outlineOffset: -2,
              transition: "background-color 0.2s ease, outline-color 0.2s ease",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha(theme.palette.text.primary, 0.12),
                borderRadius: 999,
              },
            }}
          >
            {Array.isArray(clients) && clients.length > 0 ? (
              clients.map((client, index) => (
                <PipelineCard
                  key={String(client.id)}
                  client={client}
                  index={index}
                  suppressCardOpenRef={suppressCardOpenRef}
                  dragWidth={dragSizeRef?.current?.width || AWARENESS_CARD_FALLBACK_WIDTH}
                  isSourceGhost={draggingId === String(client.id)}
                  stageName={stage.nome}
                  onOpenClient={onOpenClient}
                />
              ))
            ) : (
              <Typography variant="body2" color={theme.palette.text.secondary} sx={{ textAlign: "center", py: 3, px: 1 }}>
                {t("pipelinePage.cardNoClientsInStage")}
              </Typography>
            )}
            {hasMore && Array.isArray(clients) && clients.length > 0 ? (
              <Button
                fullWidth
                size="small"
                variant="text"
                onClick={onLoadMore}
                disabled={loadingMore}
                sx={{ mt: 1 }}
              >
                {loadingMore ? <CircularProgress size={18} /> : "Carregar mais"}
              </Button>
            ) : null}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </Box>
  );
};

const AwarenessClientCard = ({ client, isDragging, onOpen }) => {
  const theme = useTheme();
  const { t } = useTranslation("settings");
  const phoneStr = formatClientDisplayPhone(client) || null;
  const isInactiveClient = Number(client.status) === 0;
  const showContactRow = Boolean(phoneStr) || isInactiveClient;
  const registeredLabel =
    client.data_cadastro &&
    t("pipelinePage.cardRegisteredOn", {
      date: new Date(client.data_cadastro).toLocaleDateString(),
    });
  const { count: relationshipsCount, hint: relationshipHint } =
    getClientListRelationshipProps(client);
  const displayName = String(client.nome || "").trim() || t("pipelinePage.cardNoName");
  const emailStr = String(client.email || "").trim();

  return (
    <Card
      elevation={0}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      tabIndex={onOpen ? 0 : undefined}
      role={onOpen ? "button" : undefined}
      aria-label={
        onOpen
          ? `${t("pipelinePage.cardOpenDetails")}. ${t("pipelinePage.cardDragHint")}`
          : undefined
      }
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, isDragging ? 0.9 : 1)}`,
        borderRadius: 2,
        boxShadow: isDragging
          ? "none"
          : theme.palette.mode === "dark"
            ? `0 2px 8px ${alpha(theme.palette.common.black, 0.35)}`
            : `0 2px 10px ${alpha(theme.palette.common.black, 0.06)}`,
        outline: "none",
        transition: isDragging
          ? "none"
          : theme.transitions.create(["box-shadow", "border-color"], {
              duration: theme.transitions.duration.shorter,
            }),
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.45),
          boxShadow: isDragging
            ? "none"
            : `0 6px 20px ${alpha(theme.palette.primary.main, 0.12)}`,
        },
        "&:focus-visible": {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
        },
      }}
    >
      <Box
        sx={{
          height: 3,
          width: "100%",
          bgcolor: theme.palette.primary.main,
          opacity: isDragging ? 1 : 0.85,
        }}
      />
      <CardContent sx={{ pt: 1.5, pb: 1.5, overflow: "hidden", minWidth: 0, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
          <Avatar
            src={client?.imagem || undefined}
            sx={{
              width: 44,
              height: 44,
              fontSize: "0.95rem",
              fontWeight: 700,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              flexShrink: 0,
            }}
          >
            {!client?.imagem && getClientInitials(client.nome)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
            <OverflowTooltip title={displayName}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="text.primary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                }}
              >
                {displayName}
              </Typography>
            </OverflowTooltip>
            {relationshipsCount > 0 ? (
              <Box sx={{ mt: 0.5, minWidth: 0, maxWidth: "100%" }}>
                <ClientRelationshipBadge
                  count={relationshipsCount}
                  hint={relationshipHint}
                  clientId={client.id}
                  sx={{
                    maxWidth: "100%",
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      overflowWrap: "anywhere",
                    },
                  }}
                />
              </Box>
            ) : null}
            {emailStr ? (
              <OverflowTooltip title={emailStr}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {emailStr}
                </Typography>
              </OverflowTooltip>
            ) : null}
          </Box>
          <Tooltip title={t("pipelinePage.cardDragHint")}>
            <Box
              component="span"
              aria-hidden
              sx={{
                display: "inline-flex",
                mt: -0.25,
                mr: -0.5,
                p: 0.5,
                color: "text.secondary",
                flexShrink: 0,
                borderRadius: 1,
                pointerEvents: "none",
              }}
            >
              <DragIndicatorOutlinedIcon fontSize="small" />
            </Box>
          </Tooltip>
        </Box>

        {showContactRow && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5, rowGap: 0.5, minWidth: 0 }}>
            {phoneStr ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary", minWidth: 0, maxWidth: "100%" }}>
                <PhoneOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  {phoneStr}
                </Typography>
              </Box>
            ) : null}
            {isInactiveClient ? (
              <Chip
                label={t("pipelinePage.cardInactive")}
                size="small"
                variant="outlined"
                sx={{ height: 22 }}
              />
            ) : null}
          </Stack>
        )}

        <CardTagsRow clientTags={client.tags} sx={{ mt: 1.25, minWidth: 0, maxWidth: "100%" }} />

        <Divider sx={{ my: 1.25, borderColor: alpha(theme.palette.divider, 0.85) }} />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
          {registeredLabel ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, color: "text.secondary" }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 15, flexShrink: 0 }} />
              <Typography variant="caption" noWrap title={registeredLabel} sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                {registeredLabel}
              </Typography>
            </Box>
          ) : (
            <span />
          )}
          <ChevronRightRoundedIcon sx={{ fontSize: 22, color: "action.active", flexShrink: 0, opacity: 0.7 }} />
        </Box>
      </CardContent>
    </Card>
  );
};

const PipelineDragNode = ({
  provided,
  snapshot,
  client,
  suppressCardOpenRef,
  clone,
  dragWidth,
  isSourceGhost = false,
  stageName,
  onOpenClient,
}) => {
  const theme = useTheme();
  const isDragging = Boolean(snapshot?.isDragging || clone);
  const lockedWidth = isDragging ? dragWidth || AWARENESS_CARD_FALLBACK_WIDTH : undefined;

  const openPreview = () => {
    if (clone || suppressCardOpenRef?.current) return;
    onOpenClient?.(client, stageName);
  };

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={{
        ...(isDragging
          ? lockAwarenessDragStyle(provided.draggableProps.style, lockedWidth)
          : {
              ...provided.draggableProps.style,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }),
        marginBottom: clone ? 0 : 10,
      }}
    >
      <div
        style={{
          outline: "none",
          cursor: isDragging ? "grabbing" : "grab",
          transform: isDragging ? "rotate(1.5deg)" : undefined,
          filter: isDragging
            ? `drop-shadow(0 8px 16px ${alpha(theme.palette.common.black, 0.18)})`
            : undefined,
          opacity: !clone && isSourceGhost ? 0.4 : 1,
          pointerEvents: clone ? "none" : undefined,
        }}
      >
        <AwarenessClientCard
          client={client}
          isDragging={isDragging}
          onOpen={clone ? undefined : openPreview}
        />
      </div>
    </div>
  );
};

const PipelineCard = ({
  client,
  index,
  suppressCardOpenRef,
  provided: cloneProvided,
  snapshot: cloneSnapshot,
  clone = false,
  dragWidth,
  isSourceGhost = false,
  stageName,
  onOpenClient,
}) => {
  if (clone && cloneProvided) {
    return (
      <PipelineDragNode
        provided={cloneProvided}
        snapshot={cloneSnapshot}
        client={client}
        suppressCardOpenRef={suppressCardOpenRef}
        clone
        dragWidth={dragWidth}
        stageName={stageName}
        onOpenClient={onOpenClient}
      />
    );
  }

  return (
    <Draggable draggableId={String(client.id)} index={index}>
      {(provided, snapshot) => (
        <PipelineDragNode
          provided={provided}
          snapshot={snapshot}
          client={client}
          suppressCardOpenRef={suppressCardOpenRef}
          dragWidth={dragWidth}
          isSourceGhost={isSourceGhost}
          stageName={stageName}
          onOpenClient={onOpenClient}
        />
      )}
    </Draggable>
  );
};


export default Pipeline;
